<template>
	<div class="px-4 sm:px-6">
		<div class="flex justify-between items-center pb-6">
      <h2 class="amo-h2">Mileage Details</h2>
      <button type="button" title="Delete" @click="destroy" class="ml-2 bg-white rounded-full h-8 w-8 flex items-center justify-center text-red-600 hover:bg-gray-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-gray-100">
        <TrashIcon class="h-5 w-5" aria-hidden="true" />
        <span class="sr-only">Delete</span>
      </button>
		</div>
		<dl class="divide-y divide-gray-200">
			<div class="amo-details">
				<dt>Date</dt>
				<dd>
          <EditableInput v-model="expense.date" type="date" :formatter="formatDate" />
        </dd>
			</div>

			<div class="amo-details">
				<dt>Miles</dt>
				<dd>
          <EditableInput v-model="expense.miles" />
        </dd>
			</div>

      <div class="amo-details">
        <dt>Mileage Rate</dt>
        <dd>
          <div class="relative w-48">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span class="text-gray-500 sm:text-sm">$</span>
            </div>
            <CurrencyInput disabled :modelValue="rate" class="amo-input pl-7 pr-16 text-right" id="amount" aria-describedby="price-currency" />
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span class="text-gray-500 sm:text-sm" id="price-currency">/ mile</span>
            </div>
          </div>
        </dd>
      </div>

      <div class="amo-details">
        <dt>Amount</dt>
        <dd>
          <div class="relative w-48">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span class="text-gray-500 sm:text-sm">$</span>
            </div>
            <CurrencyInput disabled :modelValue="amount" class="amo-input pl-7 pr-16 text-right" id="amount" aria-describedby="price-currency" />
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span class="text-gray-500 sm:text-sm" id="price-currency">USD</span>
            </div>
          </div>
        </dd>
      </div>

      <div class="amo-details">
        <dt>Stay</dt>
        <dd>
          <StaySelect v-model="expense.stay_id" id="stay" class="ml-6 w-48 sm:w-full" />
        </dd>
      </div>

			<div class="amo-details">
				<dt>Memo</dt>
        <dd>
          <EditableInput v-model="expense.memo" type="textarea" />
        </dd>
			</div>

      <div class="amo-details-vertical">
        <dt>Attachment</dt>
        <dd>
          <AttachmentInput v-model="expense.attachment" />
        </dd>
      </div>
		</dl>
	</div>
</template>

<script>
import { ref, toRefs, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import { PaperClipIcon, PencilIcon, TrashIcon } from '@heroicons/vue/outline'
import store from '@/store'
import CurrencyInput from '@/components/CurrencyInput'
import EditableInput from '@/components/EditableInput'
import StaySelect from '@/components/StaySelect'
import AttachmentInput from '@/components/AttachmentInput'
import { formatCurrency, formatDate } from '@/utils/helpers'
import { diff } from '@/utils/diff'
import { updateExpense, destroyExpense, removeExpenseAttachment } from '@/api'
import mixpanel from '@/utils/mixpanel'

export default {
	components: {
    AttachmentInput,
    CurrencyInput,
    EditableInput,
    StaySelect,
		PaperClipIcon,
    PencilIcon,
		TrashIcon,
	},
  props: ['expense'],
  emits: ['refresh'],
  setup(props, { emit }) {
    const { expense } = toRefs(props)
    const router = useRouter()

    const rates = {
      default: 0.56,
      2021: 0.56,
      2020: 0.575,
      2019: 0.58,
      2018: 0.545,
      2017: 0.535,
      2016: 0.54,
      2015: 0.575,
      2014: 0.56,
      2013: 0.565,
      2012: 0.555,
    }

    const rate = computed(() => {
      const date = dayjs(expense.value.date, 'YYYY-MM-DD')

      if (date.isValid()) {
        const year = date.year()

        if (year in rates) {
          return rates[year]
        }
      }

      return rates.default
    })

    const amount = computed(() => {
      return (expense.value.miles * rate.value).toFixed(2)
    })

    const update = async (params) => {
      const data = new FormData()
      Object.keys(params).forEach(key => data.append(key, params[key] || ''))
      await updateExpense(expense.value.id, data)
      mixpanel.track('Edit Expense', {
        ...params,
        type: 'mileage',
      })
      emit('refresh')
      store.dispatch('expenses/getAll')
    }

    const detach = async () => {
      await removeExpenseAttachment(expense.value.id)
      emit('refresh')
      store.dispatch('expenses/getAll')
    }

    const destroy = async () => {
      await destroyExpense(expense.value.id)
      mixpanel.track('Delete Expense', { type: 'mileage' })
      store.dispatch('expenses/getAll')
      router.push({ name: 'expenses' })
    }

    watch(
      () => cloneDeep(expense.value),
      (current, previous) => {
        const params = diff(previous, current)

        delete params['category'] // Hackety, hack, hack

        // Don't do anything if expense is loading for the first time
        if (isEmpty(previous)) {
          return
        }

        // Special treatment of attachments
        //   - If null, explicitly remove the attachment
        //   - If File, allow the attachment to be updated normally
        //   - If string (else), it means the attachment was likely recently
        //     updated and nothing new should happen
        if (params.hasOwnProperty('attachment')) {
          if (!params.attachment) {
            detach()
          }

          // If we're not updating the attachment, get rid of it!
          if (!(params.attachment instanceof File)) {
            delete params['attachment']
          }
        }

        if (!isEmpty(params)) {
          update(params)
        }
      }
    )

    return {
      expense,
      destroy,
      close: () => router.push({ name: 'expenses' }),
      formatCurrency,
      formatDate,
      rate,
      amount,
    }
  }
}
</script>
